import React, { useMemo, useCallback } from 'react'
import { HStack, Button, Tooltip } from '@chakra-ui/react'
import { FiChevronRight, FiHome } from 'react-icons/fi'
import { Folder } from '@/types'
import { useAppTranslation } from '@/contexts/LanguageContext'

interface BreadcrumbSegment {
  name: string
  id: number | null
  path: string
}

interface BreadcrumbProps {
  path?: string
  showRoot?: boolean
  folderTree?: Folder[]
  onFolderSelect?: (folder: Folder | null) => void
}

export default function Breadcrumb({
  path,
  showRoot = true,
  folderTree = [],
  onFolderSelect,
}: BreadcrumbProps) {
  const { t } = useAppTranslation('common')

  // Parse the path into segments - memoized
  const pathSegments = useMemo(() => {
    if (!path) return []

    // Remove leading slash and split by '/'
    return path.replace(/^\//, '').split('/').filter(Boolean)
  }, [path])

  // Find folder by path traversal - memoized
  const folderSegments = useMemo((): BreadcrumbSegment[] => {
    if (pathSegments.length === 0) return []

    const segments: BreadcrumbSegment[] = []
    let currentFolders = folderTree
    let currentPath = ''

    for (let i = 0; i < pathSegments.length; i++) {
      const segmentName = pathSegments[i]
      currentPath = currentPath + '/' + segmentName

      const folder = currentFolders.find((f) => f.name === segmentName)
      if (folder) {
        segments.push({
          name: folder.name,
          id: folder.id,
          path: currentPath,
        })
        currentFolders = folder.subfolders || folder.children || []
      } else {
        // If we can't find the exact folder, just use the name
        segments.push({
          name: segmentName,
          id: null,
          path: currentPath,
        })
        // Stop traversing if we can't find the folder
        break
      }
    }

    return segments
  }, [pathSegments, folderTree])

  // Find folder by ID in tree - memoized
  const findFolderById = useMemo(() => {
    const findFolder = (folders: Folder[], targetId: number): Folder | null => {
      for (const folder of folders) {
        if (folder.id === targetId) return folder
        if (folder.subfolders || folder.children) {
          const found = findFolder(folder.subfolders || folder.children || [], targetId)
          if (found) return found
        }
      }
      return null
    }
    return findFolder
  }, [])

  // Handle navigation - memoized callback
  const handleNavigateToFolder = useCallback(
    (folderId: number | null) => {
      if (!onFolderSelect) return

      if (folderId === null) {
        onFolderSelect(null) // Navigate to root
      } else {
        const folder = findFolderById(folderTree, folderId)
        if (folder) {
          onFolderSelect(folder)
        }
      }
    },
    [folderTree, findFolderById, onFolderSelect]
  )

  // Handle root navigation - memoized callback
  const handleRootNavigation = useCallback(() => {
    handleNavigateToFolder(null)
  }, [handleNavigateToFolder])

  // Don't render if no root and no path
  if (!showRoot && pathSegments.length === 0) {
    return null
  }

  return (
    <HStack spacing={1} fontSize="sm" color="gray.600" flexWrap="wrap">
      {/* Root/Home */}
      {showRoot && (
        <>
          <Button
            size="xs"
            variant="ghost"
            // leftIcon={<FiHome size="12px" />}
            onClick={handleRootNavigation}
            color="gray.600"
            _hover={{ color: 'blue.600', bg: 'blue.50' }}
            minW="auto"
            h="auto"
            py={1}
            px={2}
          >
            {t('navigation.home')}
          </Button>
          {pathSegments.length > 0 && <FiChevronRight size="12px" color="gray.400" />}
        </>
      )}

      {/* Path segments */}
      {folderSegments.map((segment, index) => (
        <React.Fragment key={segment.path}>
          <Tooltip label={segment.name} placement="top" hasArrow>
            <Button
              size="xs"
              variant="ghost"
              onClick={() => segment.id && handleNavigateToFolder(segment.id)}
              color="gray.600"
              _hover={{ color: 'blue.600', bg: 'blue.50' }}
              isDisabled={!segment.id || !onFolderSelect}
              minW="auto"
              h="auto"
              py={1}
              px={2}
              maxW="200px"
              isTruncated
            >
              {segment.name}
            </Button>
          </Tooltip>
          {index < folderSegments.length - 1 && <FiChevronRight size="12px" color="gray.400" />}
        </React.Fragment>
      ))}
    </HStack>
  )
}
